import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import AlertModal from '../common/AlertModal';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS_FROM = Array.from({ length: 25 }, (_, i) => CURRENT_YEAR - i);
const YEARS_TO = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR + i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const selectStyle = {
  flex: 1, padding: '10px 8px',
  border: '1.5px solid #e5e7eb', borderRadius: '8px',
  fontSize: '15px', background: '#fff', color: '#374151',
  WebkitAppearance: 'none', appearance: 'none',
  cursor: 'pointer', outline: 'none', fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block', fontSize: '13px', fontWeight: '600',
  color: '#374151', marginBottom: '6px',
};

const reqMark = <span style={{ color: '#ef4444', marginLeft: '2px', fontSize: '12px' }}>*</span>;

function parseDate(dateStr) {
  if (!dateStr) return { year: '', month: '', day: '' };
  const [y, m, d] = dateStr.split('-');
  return { year: String(parseInt(y, 10)), month: String(parseInt(m, 10)), day: String(parseInt(d, 10)) };
}

const ChildMedicalAidModal = ({ open, onClose, childProfile, editingAid, onSaved }) => {
  const isEdit = !!editingAid?.id;
  const [insurerNumber, setInsurerNumber] = useState('');
  const [recipientNumber, setRecipientNumber] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [fromMonth, setFromMonth] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toYear, setToYear] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [toDay, setToDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });

  useEffect(() => {
    if (!open) return;
    if (isEdit && editingAid) {
      setInsurerNumber(editingAid.insurer_number || '');
      setRecipientNumber(editingAid.recipient_number || '');
      const from = parseDate(editingAid.valid_from);
      setFromYear(from.year); setFromMonth(from.month); setFromDay(from.day);
      const to = parseDate(editingAid.valid_to);
      setToYear(to.year); setToMonth(to.month); setToDay(to.day);
    } else {
      setInsurerNumber(''); setRecipientNumber('');
      setFromYear(''); setFromMonth(''); setFromDay('');
      setToYear(''); setToMonth(''); setToDay('');
    }
  }, [open, isEdit, editingAid]);

  const isValid = insurerNumber.trim() && recipientNumber.trim() && fromYear && fromMonth && fromDay;

  const submit = async (e) => {
    e?.preventDefault();
    if (!isValid || saving) return;
    setSaving(true);

    const validFrom = `${fromYear}-${String(fromMonth).padStart(2, '0')}-${String(fromDay).padStart(2, '0')}`;
    const validTo = toYear && toMonth && toDay
      ? `${toYear}-${String(toMonth).padStart(2, '0')}-${String(toDay).padStart(2, '0')}`
      : null;

    const payload = {
      child_medical_aid: {
        insurer_number: insurerNumber.trim(),
        recipient_number: recipientNumber.trim(),
        valid_from: validFrom,
        valid_to: validTo,
        is_active: true,
      },
    };

    const url = `/api/medichat/child_profiles/${childProfile.id}/child_medical_aids`;
    const response = await (isEdit
      ? axiosInstance.put(`${url}/${editingAid.id}`, payload)
      : axiosInstance.post(url, payload)
    ).catch(err => err?.response || null);
    setSaving(false);

    if (response && response.status >= 200 && response.status < 300) {
      onSaved?.();
      onClose?.();
    } else {
      setAlertModal({ open: true, title: 'エラー', message: response?.data?.errors?.join('\n') || response?.data?.message || '保存に失敗しました' });
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 10002,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: '500px', maxHeight: '85vh',
        background: 'white', borderRadius: '20px 20px 0 0',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {/* ヘッダー */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>
            {isEdit ? '医療証を編集' : '医療証を追加'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px' }}>✕</button>
        </div>

        {/* ボディ */}
        <form onSubmit={submit} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
            <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: '10px', fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
              {childProfile?.last_name} {childProfile?.first_name} さんの子ども医療費助成証
            </div>

            <div>
              <label style={labelStyle}>負担者番号 {reqMark}</label>
              <input
                style={{ ...selectStyle, flex: 'none', width: '100%' }}
                type="text" inputMode="numeric" placeholder="例: 80137010"
                value={insurerNumber} onChange={e => setInsurerNumber(e.target.value)}
                disabled={saving}
              />
            </div>

            <div>
              <label style={labelStyle}>受給者番号 {reqMark}</label>
              <input
                style={{ ...selectStyle, flex: 'none', width: '100%' }}
                type="text" inputMode="numeric" placeholder="例: 1234567"
                value={recipientNumber} onChange={e => setRecipientNumber(e.target.value)}
                disabled={saving}
              />
            </div>

            <div>
              <label style={labelStyle}>有効期間（開始） {reqMark}</label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                <select style={selectStyle} value={fromYear} onChange={e => setFromYear(e.target.value)} disabled={saving}>
                  <option value="">年</option>
                  {YEARS_FROM.map(y => <option key={y} value={y}>{y}年</option>)}
                </select>
                <select style={selectStyle} value={fromMonth} onChange={e => setFromMonth(e.target.value)} disabled={saving}>
                  <option value="">月</option>
                  {MONTHS.map(m => <option key={m} value={m}>{m}月</option>)}
                </select>
                <select style={selectStyle} value={fromDay} onChange={e => setFromDay(e.target.value)} disabled={saving}>
                  <option value="">日</option>
                  {DAYS.map(d => <option key={d} value={d}>{d}日</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                有効期間（終了）
                <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400', marginLeft: '4px' }}>任意</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                <select style={selectStyle} value={toYear} onChange={e => setToYear(e.target.value)} disabled={saving}>
                  <option value="">年</option>
                  {YEARS_TO.map(y => <option key={y} value={y}>{y}年</option>)}
                </select>
                <select style={selectStyle} value={toMonth} onChange={e => setToMonth(e.target.value)} disabled={saving}>
                  <option value="">月</option>
                  {MONTHS.map(m => <option key={m} value={m}>{m}月</option>)}
                </select>
                <select style={selectStyle} value={toDay} onChange={e => setToDay(e.target.value)} disabled={saving}>
                  <option value="">日</option>
                  {DAYS.map(d => <option key={d} value={d}>{d}日</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* フッター */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', flexShrink: 0 }}>
            <button
              type="button" onClick={onClose}
              style={{
                flex: 1, padding: '13px', border: '1.5px solid #e5e7eb', borderRadius: '10px',
                background: '#f9fafb', color: '#6b7280', fontSize: '15px', fontWeight: '500', cursor: 'pointer',
              }}
            >キャンセル</button>
            <button
              type="submit" disabled={!isValid || saving}
              style={{
                flex: 2, padding: '13px', border: 'none', borderRadius: '10px',
                background: (!isValid || saving) ? '#e5e7eb' : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                color: (!isValid || saving) ? '#9ca3af' : 'white',
                fontSize: '15px', fontWeight: '600',
                cursor: (!isValid || saving) ? 'not-allowed' : 'pointer',
              }}
            >{saving ? '保存中…' : (isEdit ? '更新する' : '登録する')}</button>
          </div>
        </form>
      </div>

      <AlertModal
        open={alertModal.open} title={alertModal.title} message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
};

export default ChildMedicalAidModal;
